import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Linkedin, Download, MapPin } from "lucide-react";
import { PROFILE } from "../../data/content";
import { downloadResume } from "../../lib/downloadResume";

export default function ProContact() {
  const mailHref = `mailto:${PROFILE.email}?subject=${encodeURIComponent(
    "Glad to connect"
  )}&body=${encodeURIComponent(PROFILE.connectMessage)}`;

  const openLinkedIn = async () => {
    try {
      await navigator.clipboard.writeText(PROFILE.connectMessage);
      toast("Message copied to clipboard", {
        description: `Paste "${PROFILE.connectMessage}" into LinkedIn — opening now.`,
      });
    } catch (e) {
      toast("Opening LinkedIn", {
        description: `Send Arya this: "${PROFILE.connectMessage}"`,
      });
    }
    window.open("https://www.linkedin.com/messaging/compose/", "_blank", "noopener,noreferrer");
  };

  const openResume = async () => {
    if (!PROFILE.resumeUrl) {
      toast("Resume not available yet");
      return;
    }
    toast("Downloading résumé", { description: PROFILE.resumeFilename });
    await downloadResume();
  };

  return (
    <div className="pt-24 pb-16" data-testid="pro-contact">
      <section className="max-w-5xl mx-auto px-5 md:px-8">
        <p className="font-plexMono text-xs uppercase tracking-[0.2em] text-terracotta">Contact</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-neutral-900 mt-3">
          Let&apos;s work together.
        </h1>
        <p className="font-plex text-neutral-600 mt-4 max-w-2xl">
          Always up for a good conversation — swap notes on Business Central, trade hypercare war
          stories, or just say hi. Pick whichever inbox is easiest.
        </p>

        {/* CONTACT OPTIONS */}
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          <motion.a
            href={mailHref}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="group border border-neutral-200 rounded-2xl p-8 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            data-testid="pro-contact-mail"
          >
            <div className="w-11 h-11 rounded-xl bg-neutral-100 group-hover:bg-white/10 flex items-center justify-center mb-6 transition-colors">
              <Mail size={22} />
            </div>
            <p className="font-plexMono text-xs uppercase tracking-widest text-terracotta">
              Option 01 — Email
            </p>
            <h2 className="font-serif text-2xl md:text-3xl mt-2">Write me an email</h2>
            <p className="font-plex text-sm mt-3 opacity-80">
              Opens your mail app addressed to {PROFILE.email}, pre-filled with a friendly hello.
            </p>
          </motion.a>

          <motion.button
            onClick={openLinkedIn}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group border border-neutral-200 rounded-2xl p-8 text-left hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            data-testid="pro-contact-linkedin"
          >
            <div className="w-11 h-11 rounded-xl bg-neutral-100 group-hover:bg-white/10 flex items-center justify-center mb-6 transition-colors">
              <Linkedin size={22} />
            </div>
            <p className="font-plexMono text-xs uppercase tracking-widest text-terracotta">
              Option 02 — LinkedIn
            </p>
            <h2 className="font-serif text-2xl md:text-3xl mt-2">Message on LinkedIn</h2>
            <p className="font-plex text-sm mt-3 opacity-80">
              Copies “{PROFILE.connectMessage}” to your clipboard and opens LinkedIn — paste & send.
            </p>
          </motion.button>
        </div>

        {/* DETAILS + RESUME */}
        <div className="mt-14 border-t border-neutral-200 pt-10 grid md:grid-cols-2 gap-8">
          <div>
            <p className="font-plexMono text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Direct
            </p>
            <a
              href={PROFILE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="font-plex text-neutral-900 flex items-center gap-2 hover:text-terracotta transition-colors"
              data-testid="pro-contact-linkedin-link"
            >
              <Linkedin size={14} className="text-neutral-400" /> LinkedIn — Arya Kadulkar
            </a>
            <p className="font-plex text-neutral-900 mt-1 flex items-center gap-2">
              <MapPin size={14} className="text-neutral-400" /> {PROFILE.location}
            </p>
          </div>
          <div>
            <p className="font-plexMono text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Résumé
            </p>
            <button
              onClick={openResume}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white px-5 py-3 rounded-full text-sm font-plex font-medium hover:bg-terracotta transition-colors"
              data-testid="pro-contact-resume"
            >
              <Download size={16} /> Download résumé
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
